import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { places } from '@/mocks/placesData';
import { useTrip } from '@/context/TripContext';

export default function PlaceDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isInTrip, toggleSpot } = useTrip();
  const place = places.find((p) => p.id === id);
  const inTrip = id ? isInTrip(id) : false;
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  const handleAddToTrip = () => {
    if (!id) return;
    const added = toggleSpot(id);
    if (added) {
      showToast('已加入行程');
    } else {
      showToast('已在行程中');
    }
  };

  if (!place) {
    return (
      <div className="min-h-screen bg-bg flex flex-col items-center justify-center text-text-muted">
        <i className="ri-map-pin-line text-4xl mb-3" />
        <p className="text-sm">地点不存在</p>
        <button
          className="mt-4 text-bamboo text-sm cursor-pointer"
          onClick={() => navigate('/explore')}
        >
          返回找地方
        </button>
      </div>
    );
  }

  const isFood = place.category === '吃饭';

  return (
    <div className="min-h-screen bg-bg pb-24">
      {/* Toast */}
      {toast && (
        <div className="fixed top-12 left-0 right-0 z-50 flex justify-center pointer-events-none">
          <div className="bg-text-primary/95 text-white text-sm px-4 py-2 rounded-full shadow-lg backdrop-blur-sm">
            {toast}
          </div>
        </div>
      )}

      {/* Top Image */}
      <div className="relative h-56">
        <img
          src={place.images[0]}
          alt={place.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent" />
        <button
          className="absolute top-3 left-3 w-9 h-9 rounded-full bg-white/80 flex items-center justify-center cursor-pointer"
          onClick={() => navigate(-1)}
        >
          <i className="ri-arrow-left-line text-text-body" />
        </button>
      </div>

      {/* Info */}
      <div className="px-4 -mt-4 relative z-10">
        <div className="bg-white rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-lg font-bold text-text-primary">{place.name}</h1>
              <p className="text-xs text-text-muted mt-0.5">
                {place.area} · {place.category}
              </p>
            </div>
            <div className="flex gap-1.5">
              {place.status.slice(0, 2).map((s) => (
                <span
                  key={s}
                  className="text-[10px] px-2 py-0.5 rounded bg-bamboo-pale text-bamboo"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-3 space-y-2">
            <div className="flex items-start gap-2">
              <i className="ri-map-pin-line text-text-muted text-sm mt-0.5" />
              <span className="text-sm text-text-body">{place.address}</span>
            </div>
            <div className="flex items-start gap-2">
              <i className="ri-time-line text-text-muted text-sm mt-0.5" />
              <span className="text-sm text-text-body">{place.hours}</span>
            </div>
            <div className="flex items-start gap-2">
              <i className="ri-money-cny-circle-line text-text-muted text-sm mt-0.5" />
              <span className="text-sm text-text-body">{place.price}</span>
            </div>
            <div className="flex items-start gap-2">
              <i className="ri-hourglass-line text-text-muted text-sm mt-0.5" />
              <span className="text-sm text-text-body">
                建议游玩时长：{place.duration}
              </span>
            </div>
            <div className="flex items-start gap-2">
              <i className="ri-parking-line text-text-muted text-sm mt-0.5" />
              <span className="text-sm text-text-body">{place.parking}</span>
            </div>
          </div>

          {/* Status badges */}
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-divider">
            {place.status.map((s) => (
              <span
                key={s}
                className="flex items-center gap-1 text-xs text-bamboo"
              >
                <i className="ri-check-line" />
                {s}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Fixed Tips */}
      <div className="px-4 mt-3">
        <div className="bg-white rounded-xl p-4">
          <h3 className="text-sm font-bold text-text-primary flex items-center gap-1.5">
            <i className="ri-lightbulb-line text-tea" />
            固定提醒
          </h3>
          <div className="mt-2 space-y-2">
            {place.tips.map((tip, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-bamboo-light text-sm mt-0.5">•</span>
                <p className="text-sm text-text-body">{tip}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Photo Spots */}
      {place.photoSpots && place.photoSpots.length > 0 && (
        <div className="px-4 mt-3">
          <div className="bg-white rounded-xl p-4">
            <h3 className="text-sm font-bold text-text-primary flex items-center gap-1.5">
              <i className="ri-camera-line text-stream" />
              拍照点
            </h3>
            <div className="flex flex-wrap gap-2 mt-2">
              {place.photoSpots.map((spot) => (
                <span
                  key={spot}
                  className="px-2.5 py-1 rounded-full bg-stream/10 text-stream text-xs"
                >
                  {spot}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Specialties (for food places) */}
      {isFood && place.specialties && (
        <div className="px-4 mt-3">
          <div className="bg-white rounded-xl p-4">
            <h3 className="text-sm font-bold text-text-primary flex items-center gap-1.5">
              <i className="ri-restaurant-line text-bamboo" />
              特色菜
            </h3>
            <div className="flex flex-wrap gap-2 mt-2">
              {place.specialties.map((dish) => (
                <span
                  key={dish}
                  className="px-2.5 py-1 rounded-full bg-bamboo-pale text-bamboo text-xs"
                >
                  {dish}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* More images */}
      {place.images.length > 1 && (
        <div className="px-4 mt-3">
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {place.images.slice(1).map((img, i) => (
              <img
                key={i}
                src={img}
                alt={`${place.name} 照片 ${i + 2}`}
                className="w-28 h-20 rounded-lg object-cover flex-shrink-0"
              />
            ))}
          </div>
        </div>
      )}

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-divider px-4 py-3">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <button className="flex flex-col items-center justify-center w-12 cursor-pointer">
            <i className="ri-heart-line text-text-body text-lg" />
            <span className="text-[10px] text-text-muted mt-0.5">收藏</span>
          </button>
          <button
            className="flex flex-col items-center justify-center w-12 cursor-pointer"
            onClick={() => {
              setToast(`导航至${place.name}`);
              setTimeout(() => setToast(''), 2000);
            }}
          >
            <i className="ri-map-pin-line text-text-body text-lg" />
            <span className="text-[10px] text-text-muted mt-0.5">导航</span>
          </button>
          <button
            className={`flex-1 py-2.5 rounded-lg font-medium text-sm text-white cursor-pointer transition-colors ${
              inTrip
                ? 'bg-bamboo/60'
                : 'bg-bamboo'
            }`}
            onClick={handleAddToTrip}
          >
            {inTrip ? '已在行程中' : '加入行程'}
          </button>
        </div>
      </div>
    </div>
  );
}